import { Icon } from "@iconify/react";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiRequest } from "../../../redux/api";
import { buildAppLink } from "../../../utils/landingRedirect";

interface StripePrice {
  id: string;
  amount: number | null;
  currency: string;
  interval?: string;
  intervalCount?: number;
  metadata?: Record<string, string>;
}

type PaidPlanId = "plus" | "pro" | "max";
type PlanId = "free" | PaidPlanId;
type BillingInterval = "monthly" | "yearly";

type TierPrices = Record<
  PaidPlanId,
  Record<BillingInterval, StripePrice | null>
>;

type DisplayPrice = {
  currency: string;
  monthlyAmount: string;
  yearlyAmount: string;
  yearlyMonthlyAmount: string;
  yearlySavingsPercent: number | null;
};

const EMPTY_TIER_PRICES: TierPrices = {
  plus: { monthly: null, yearly: null },
  pro: { monthly: null, yearly: null },
  max: { monthly: null, yearly: null },
};

const PRODUCTION_PUBLIC_API_ENDPOINT = "https://api-server.youwo.ai/";

const CHINA_PRICE_AMOUNTS: Record<
  PaidPlanId,
  Record<BillingInterval, number>
> = {
  plus: { monthly: 3500, yearly: 30000 },
  pro: { monthly: 6800, yearly: 68800 },
  max: { monthly: 20000, yearly: 204800 },
};

const INTERNATIONAL_FALLBACK_PRICE_AMOUNTS: Record<
  string,
  Record<PaidPlanId, Record<BillingInterval, number>>
> = {
  USD: {
    plus: { monthly: 1999, yearly: 19999 },
    pro: { monthly: 4999, yearly: 49999 },
    max: { monthly: 9999, yearly: 99999 },
  },
  CAD: {
    plus: { monthly: 2499, yearly: 24999 },
    pro: { monthly: 5999, yearly: 59999 },
    max: { monthly: 11999, yearly: 119999 },
  },
};

const FALLBACK_MONTHLY_CREDITS: Record<
  "global" | "CN",
  Record<PaidPlanId, number>
> = {
  global: {
    plus: 21000,
    pro: 55000,
    max: 1200000,
  },
  CN: {
    plus: 8000,
    pro: 18000,
    max: 60000,
  },
};

const getForwardedPricingQuery = (extraParams?: Record<string, string>) => {
  if (typeof window === "undefined") return "";

  const current = new URLSearchParams(window.location.search);
  const forwarded = new URLSearchParams();
  const currency = current.get("currency");
  const forceCny = current.get("forceCny");

  if (currency) forwarded.set("currency", currency);
  if (forceCny === "1") forwarded.set("forceCny", "1");
  Object.entries(extraParams ?? {}).forEach(([key, value]) => {
    forwarded.set(key, value);
  });

  const query = forwarded.toString();
  return query ? `?${query}` : "";
};

const getForcedCurrency = () => {
  if (typeof window === "undefined") return null;

  const current = new URLSearchParams(window.location.search);
  if (current.get("forceCny") === "1") return "CNY";

  return current.get("currency")?.toUpperCase() || null;
};

const formatAmount = (cents: number) => {
  const amount = cents / 100;
  const hasCents = Math.round(cents) % 100 !== 0;

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  }).format(amount);
};

const formatMonthlyEquivalentAmount = (cents: number) => {
  const amount = cents / 100;
  if (amount >= 20) {
    return new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(Math.round(amount));
  }
  return formatAmount(cents);
};

const getYearlySavingsPercent = (monthlyCents: number, yearlyCents: number) => {
  const yearlyIfMonthly = monthlyCents * 12;
  if (yearlyIfMonthly <= 0 || yearlyCents >= yearlyIfMonthly) return null;
  return Math.round(((yearlyIfMonthly - yearlyCents) / yearlyIfMonthly) * 100);
};

const currencySymbol = (currency?: string) => {
  const code = currency?.toUpperCase();
  if (code === "CNY" || code === "JPY") return "¥";
  if (code === "CAD") return "C$";
  if (code === "EUR") return "€";
  if (code === "GBP") return "£";
  return "$";
};

const getPlanFallbackCurrency = (currency?: string) => {
  const normalized = currency?.toUpperCase() || "USD";
  return INTERNATIONAL_FALLBACK_PRICE_AMOUNTS[normalized] ? normalized : "USD";
};

const getPricingLink = (plan?: PaidPlanId) =>
  buildAppLink(
    `/pricing${getForwardedPricingQuery(plan ? { plan } : undefined)}`,
  );

const isLocalApiEndpoint = () => {
  const endpoint = process.env.REACT_APP_API_ENDPOINT || "";
  return endpoint.includes("localhost") || endpoint.includes("127.0.0.1");
};

const fetchProductionPublicApi = async (url: string) => {
  const response = await fetch(`${PRODUCTION_PUBLIC_API_ENDPOINT}${url}`);
  if (!response.ok) {
    throw new Error(`Production pricing request failed: ${response.status}`);
  }
  return response.json();
};

const requestPublicPricingApi = async (url: string) => {
  try {
    return await apiRequest({
      url,
      method: "GET",
      useAuthHeader: false,
    });
  } catch (error) {
    if (!isLocalApiEndpoint()) {
      throw error;
    }
    return fetchProductionPublicApi(url);
  }
};

const PricingSection: React.FC = () => {
  const { t } = useTranslation();
  const [stripePrices, setStripePrices] =
    useState<TierPrices>(EMPTY_TIER_PRICES);
  const [marketCurrency, setMarketCurrency] = useState<string>("USD");
  const [priceCurrency, setPriceCurrency] = useState<string>("USD");
  const [billingInterval, setBillingInterval] =
    useState<BillingInterval>("yearly");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setIsLoading(true);

        const forcedCurrency = getForcedCurrency();
        const pricingQuery = getForwardedPricingQuery();
        const [marketResult, pricesResult] = await Promise.allSettled([
          forcedCurrency
            ? Promise.resolve({ currency: forcedCurrency })
            : requestPublicPricingApi("subscriptions/detect-currency"),
          requestPublicPricingApi(
            `subscriptions/stripe-prices-public${pricingQuery}`,
          ),
        ]);

        if (cancelled) return;

        if (marketResult.status === "fulfilled") {
          const detectedCurrency = marketResult.value?.currency?.toUpperCase();
          if (detectedCurrency) {
            setMarketCurrency(detectedCurrency);
          }
        }

        if (pricesResult.status === "fulfilled") {
          const res = pricesResult.value;
          if (res.currency) {
            setPriceCurrency(res.currency.toUpperCase());
          }
          if (res.tiers) {
            setStripePrices({
              plus: {
                monthly: res.tiers.plus?.monthly || null,
                yearly: res.tiers.plus?.yearly || null,
              },
              pro: {
                monthly: res.tiers.pro?.monthly || null,
                yearly: res.tiers.pro?.yearly || null,
              },
              max: {
                monthly: res.tiers.max?.monthly || null,
                yearly: res.tiers.max?.yearly || null,
              },
            });
          }
        }
      } catch {
        // Keep local fallback prices.
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const isChinaPricing = marketCurrency === "CNY";
  const displayCurrency = isChinaPricing ? "CNY" : priceCurrency;
  const formatCredits = (credits: number) =>
    new Intl.NumberFormat("en-US").format(credits);
  const getMonthlyCredits = (plan: PaidPlanId) => {
    const metadata =
      stripePrices[plan].monthly?.metadata ??
      stripePrices[plan].yearly?.metadata;
    const fromMetadata = metadata?.monthlyCredits
      ? Number.parseInt(metadata.monthlyCredits.replace(/,/g, ""), 10)
      : NaN;
    if (Number.isFinite(fromMetadata) && fromMetadata >= 0) {
      return fromMetadata;
    }
    return FALLBACK_MONTHLY_CREDITS[isChinaPricing ? "CN" : "global"][plan];
  };
  const monthlyCreditFeature = (plan: PaidPlanId) =>
    t("modern.pricing.monthlyCredits", {
      credits: formatCredits(getMonthlyCredits(plan)),
      defaultValue: `${formatCredits(getMonthlyCredits(plan))} monthly AI credits`,
    });
  const fallbackCurrency = getPlanFallbackCurrency(displayCurrency);

  const getDisplayPrice = (tier: PaidPlanId): DisplayPrice => {
    if (isChinaPricing) {
      return {
        currency: "CNY",
        monthlyAmount: formatAmount(CHINA_PRICE_AMOUNTS[tier].monthly),
        yearlyAmount: formatAmount(CHINA_PRICE_AMOUNTS[tier].yearly),
        yearlyMonthlyAmount: formatMonthlyEquivalentAmount(
          CHINA_PRICE_AMOUNTS[tier].yearly / 12,
        ),
        yearlySavingsPercent: getYearlySavingsPercent(
          CHINA_PRICE_AMOUNTS[tier].monthly,
          CHINA_PRICE_AMOUNTS[tier].yearly,
        ),
      };
    }

    const monthly = stripePrices[tier].monthly;
    const yearly = stripePrices[tier].yearly;
    const fallback =
      INTERNATIONAL_FALLBACK_PRICE_AMOUNTS[fallbackCurrency][tier];
    const monthlyCents = monthly?.amount ?? fallback.monthly;
    const yearlyCents = yearly?.amount ?? fallback.yearly;
    const currency =
      monthly?.currency?.toUpperCase() ||
      yearly?.currency?.toUpperCase() ||
      fallbackCurrency;

    return {
      currency,
      monthlyAmount: formatAmount(monthlyCents),
      yearlyAmount: formatAmount(yearlyCents),
      yearlyMonthlyAmount: formatMonthlyEquivalentAmount(yearlyCents / 12),
      yearlySavingsPercent: getYearlySavingsPercent(monthlyCents, yearlyCents),
    };
  };

  const plans = [
    {
      id: "free" as PlanId,
      name: t("modern.pricing.free.name", "Free"),
      subtitle: t(
        "modern.pricing.free.subtitle",
        "Daily credits with Basic model selection",
      ),
      buttonText: t("modern.pricing.free.button", "Choose Plan"),
      buttonLink: buildAppLink(),
      price: null,
      features: [
        t("modern.pricing.free.f1", "500 daily refresh credits"),
        t(
          "modern.pricing.free.f2",
          "Capture, chat, and study tools with daily credits",
        ),
        t(
          "modern.pricing.free.f3",
          "Basic model picker: GPT-5.4, Claude Haiku, Gemini Flash",
        ),
        t(
          "modern.pricing.free.f4",
          "Recording and imports use available AI credits",
        ),
      ],
    },
    {
      id: "plus" as PlanId,
      name: t("modern.pricing.plus.name", "Plus"),
      subtitle: t(
        "modern.pricing.plus.subtitle",
        "21,000 monthly credits, medium thinking, and larger uploads",
      ),
      buttonText: t("modern.pricing.plus.button", "Try free for 1 week"),
      buttonLink: getPricingLink("plus"),
      price: getDisplayPrice("plus"),
      features: [
        monthlyCreditFeature("plus"),
        t(
          "modern.pricing.plus.f2",
          "Medium thinking on GPT-5.4, Claude, and Gemini basic models",
        ),
        t("modern.pricing.plus.f3", "Unlimited summary, flashcard, quizzes"),
        t(
          "modern.pricing.plus.f4",
          "Recording, translation, and imports are billed from credits",
        ),
        t(
          "modern.pricing.plus.f5",
          "Larger uploads and source-backed study tools",
        ),
        t(
          "modern.pricing.plus.f6",
          "Space AI Chat, gain insight from materials",
        ),
      ],
    },
    {
      id: "pro" as PlanId,
      name: t("modern.pricing.pro.name", "Pro"),
      subtitle: t(
        "modern.pricing.pro.subtitle",
        "55,000 monthly credits with premium models, AI Agent, and Workflow automations",
      ),
      buttonText: t("modern.pricing.pro.button", "Try free for 1 week"),
      buttonLink: getPricingLink("pro"),
      price: getDisplayPrice("pro"),
      popular: true,
      features: [
        t("modern.pricing.pro.f0", "All Plus benefits and:"),
        monthlyCreditFeature("pro"),
        t(
          "modern.pricing.pro.f2",
          "Advanced models: Opus 4.8, Gemini Pro, Grok, and GPT-5.5",
        ),
        t(
          "modern.pricing.pro.f3",
          "Fast transcription and translation use credits",
        ),
        t(
          "modern.pricing.pro.f4",
          "AI Agent access and 3 workflow automations",
        ),
        t(
          "modern.pricing.pro.f5",
          "Recognize Graph/Table/Formula/Code from PDF",
        ),
        t(
          "modern.pricing.pro.f6",
          "Higher document, image, and audio import limits",
        ),
      ],
    },
    {
      id: "max" as PlanId,
      name: t("modern.pricing.max.name", "Max"),
      subtitle: t(
        "modern.pricing.max.subtitle",
        "Heavy agent work with all premium models",
      ),
      buttonText: t("modern.pricing.max.button", "Try free for 1 week"),
      buttonLink: getPricingLink("max"),
      price: getDisplayPrice("max"),
      features: [
        t("modern.pricing.max.f1", "1,200,000 monthly AI credits"),
        t("modern.pricing.max.f2", "All premium models"),
        t("modern.pricing.max.f3", "Highest workflow and agent limits"),
        t(
          "modern.pricing.max.f4",
          "Long recordings, translation, and agent runs",
        ),
        t(
          "modern.pricing.max.f5",
          "Highest document, image, and audio import limits",
        ),
        t("modern.pricing.max.f6", "Import PDFs up to 5,000 pages"),
      ],
    },
  ];
  const maxYearlySavingsPercent = Math.max(
    ...plans.map((plan) => plan.price?.yearlySavingsPercent ?? 0),
  );
  const getSavingsLabel = (percentage: number) =>
    t("modern.pricing.savePercentageOff", {
      percentage,
      defaultValue: `${percentage}% OFF`,
    });

  return (
    <section className="scroll-mt-20 bg-gray-50 py-10 md:py-14" id="pricing">
      <div className="mx-auto w-full max-w-[1640px] px-5 lg:px-7">
        <motion.div
          className="mb-6 text-center"
          initial={{ opacity: 0, y: 20 }}
          viewport={{ once: true }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <h2 className="mb-2 text-2xl font-bold text-gray-900 md:text-[30px]">
            {t("modern.pricing.title", "Choose Your Plan")}
          </h2>
          <p className="text-sm text-gray-500 md:text-[15px]">
            {t(
              "modern.pricing.subtitle",
              "Start free. Annual shows the discounted monthly equivalent; monthly is available any time.",
            )}
          </p>
        </motion.div>

        <div
          aria-label={String(
            t("modern.pricing.billingToggleLabel", "Billing interval"),
          )}
          className="mx-auto mb-6 flex w-full max-w-[340px] rounded-[10px] bg-[#eef2f6] p-1"
          role="group"
        >
          {(["monthly", "yearly"] as BillingInterval[]).map((interval) => {
            const isSelected = billingInterval === interval;
            return (
              <button
                aria-pressed={isSelected}
                className={`relative flex min-h-[42px] flex-1 items-center justify-center rounded-[10px] border-0 px-4 text-[14px] font-semibold transition-colors ${
                  isSelected
                    ? "bg-white text-[#0d0d0d] shadow-[0_2px_6px_rgba(0,0,0,0.10)]"
                    : "bg-transparent text-[#5f6368] hover:text-[#0d0d0d]"
                }`}
                key={interval}
                onClick={() => setBillingInterval(interval)}
                type="button"
              >
                {interval === "monthly"
                  ? t("modern.pricing.monthlyLabel", "Monthly")
                  : t("modern.pricing.yearlyLabel", "Yearly")}
                {interval === "yearly" &&
                  billingInterval !== "yearly" &&
                  maxYearlySavingsPercent > 0 && (
                    <span className="absolute -right-1 -top-2 rounded-full bg-[#1dbb65] px-2 py-0.5 text-[9px] font-bold uppercase leading-none text-white">
                      {getSavingsLabel(maxYearlySavingsPercent)}
                    </span>
                  )}
              </button>
            );
          })}
        </div>

        <div className="mx-auto grid max-w-[1560px] grid-cols-1 items-stretch gap-4 md:grid-cols-2 2xl:grid-cols-4 xl:gap-5">
          {plans.map((plan, i) => (
            <motion.div
              className={`relative flex min-h-0 flex-col overflow-visible rounded-2xl border-2 border-solid shadow-[0_4px_12px_rgba(0,0,0,0.05)] ${
                plan.popular
                  ? "border-[#8b5cf6] bg-white"
                  : "border-[#e0e0e0] bg-white"
              }`}
              initial={{ opacity: 0, y: 30 }}
              key={plan.id}
              transition={{ delay: i * 0.08 }}
              viewport={{ once: true }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <div className="flex flex-1 flex-col p-5">
                <div className="mb-1.5 flex min-h-[30px] items-center gap-2">
                  <h3 className="m-0 text-[22px] font-bold text-gray-900">
                    {plan.name}
                  </h3>
                  {plan.popular && (
                    <span className="rounded-md bg-[#8b5cf61a] px-2 py-1 text-[11px] font-bold uppercase leading-none text-[#8b5cf6]">
                      {t("modern.pricing.popular", "Most Popular")}
                    </span>
                  )}
                </div>
                <p className="mb-3 mt-0 min-h-[36px] text-[13px] leading-[18px] text-gray-600">
                  {plan.subtitle}
                </p>

                <div className="mb-3.5 min-h-[78px]">
                  {plan.price ? (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className="text-[18px] font-bold text-gray-900">
                          {currencySymbol(plan.price.currency)}
                        </span>
                        <span className="text-[42px] font-bold leading-[48px] text-gray-900">
                          {isLoading
                            ? "···"
                            : billingInterval === "yearly"
                              ? plan.price.yearlyMonthlyAmount
                              : plan.price.monthlyAmount}
                        </span>
                        <div className="ml-1.5 mt-2 flex flex-col justify-center">
                          <span className="text-[12px] font-bold uppercase leading-[14px] text-[#9ca3af]">
                            {plan.price.currency}
                          </span>
                          <span className="text-[12px] font-medium leading-[14px] text-[#9ca3af]">
                            {t("modern.pricing.monthly", "/ month")}
                          </span>
                        </div>
                      </div>
                      <div className="mt-1 flex min-h-6 items-center gap-2">
                        <span className="text-[14px] font-normal text-gray-600">
                          {billingInterval === "yearly"
                            ? t("modern.pricing.billedYearly", {
                                price: `${currencySymbol(plan.price.currency)}${
                                  isLoading ? "···" : plan.price.yearlyAmount
                                }`,
                                defaultValue: `Billed ${currencySymbol(
                                  plan.price.currency,
                                )}${
                                  isLoading ? "···" : plan.price.yearlyAmount
                                } yearly`,
                              })
                            : t(
                                "modern.pricing.billedMonthly",
                                "billed monthly",
                              )}
                        </span>
                        {billingInterval === "yearly" &&
                          plan.price.yearlySavingsPercent && (
                            <span className="rounded-md bg-[#10b98118] px-2 py-1 text-[12px] font-semibold leading-none text-[#10b981]">
                              {getSavingsLabel(plan.price.yearlySavingsPercent)}
                            </span>
                          )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-[42px] font-bold leading-[48px] text-green-500">
                        {t("modern.pricing.free.price", "Free")}
                      </div>
                      <div className="mt-2 text-[14px] font-semibold text-gray-600">
                        {t("modern.pricing.free.yearly", "Always free")}
                      </div>
                    </>
                  )}
                </div>

                <a
                  className="flex h-[43px] w-full items-center justify-center rounded-[9px] bg-black px-4 text-center text-[15px] font-semibold text-white no-underline transition-colors hover:bg-gray-800"
                  href={plan.buttonLink}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {plan.buttonText}
                </a>

                <div className="my-3.5 border-t border-solid border-gray-200" />

                <ul className="m-0 list-none space-y-2.5 p-0">
                  {plan.features.map((feature, index) => (
                    <li className="flex items-start gap-2.5" key={feature}>
                      <Icon
                        className="mt-px flex-shrink-0 text-[#10b981]"
                        icon="lucide:check"
                        width={16}
                      />
                      <span
                        className={`text-[13px] leading-[18px] text-[#475569] ${
                          index === 0 && plan.id !== "free"
                            ? "font-semibold"
                            : ""
                        }`}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
